import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  palette,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import {
  DRAFT_INSTRUCTIONS_MAX,
  type AssistantLocale,
  type DraftKind,
} from '@munifocus/contracts-gov';
import { useDraftCorrespondence } from '../../lib/ai-assistant-api';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { AiLabel } from './AiLabel';
import { currentLocale } from '../../lib/assistant-display';

/**
 * Draft a letter to the owner or a reply to the complainant, in English or
 * French, for the officer to edit. The text is theirs once it is on screen:
 * nothing is saved or sent by the portal, and copying it out is the only
 * action offered.
 */
export function AssistantDraft({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const draft = useDraftCorrespondence(fileId);
  const [kind, setKind] = useState<DraftKind>('owner_letter');
  const [locale, setLocale] = useState<AssistantLocale>(currentLocale(i18n.language));
  const [instructions, setInstructions] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!draft.data) return;
    setSubject(draft.data.subject);
    setBody(draft.data.body);
  }, [draft.data]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${body}`);
      snackbar.success(t('gov.assistant.draft.copied'));
    } catch {
      snackbar.error(t('gov.assistant.draft.copyFailed'));
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="assistant-draft-kind">{t('gov.assistant.draft.kind')}</InputLabel>
          <Select
            labelId="assistant-draft-kind"
            label={t('gov.assistant.draft.kind')}
            value={kind}
            onChange={(e) => setKind(e.target.value as DraftKind)}
          >
            <MenuItem value="owner_letter">{t('gov.assistant.draft.kinds.owner_letter')}</MenuItem>
            <MenuItem value="complainant_reply">
              {t('gov.assistant.draft.kinds.complainant_reply')}
            </MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={locale}
          onChange={(_e, value: AssistantLocale | null) => value && setLocale(value)}
          aria-label={t('gov.assistant.draft.language')}
        >
          <ToggleButton value="en">{t('gov.assistant.draft.languages.en')}</ToggleButton>
          <ToggleButton value="fr">{t('gov.assistant.draft.languages.fr')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {kind === 'complainant_reply' && (
        <Typography variant="body2" sx={{ color: palette.text.secondary }}>
          {t('gov.assistant.draft.complainantPrivacy')}
        </Typography>
      )}
      <TextField
        label={t('gov.assistant.draft.instructions')}
        helperText={t('gov.assistant.draft.instructionsHint')}
        value={instructions}
        onChange={(e) => setInstructions(e.target.value.slice(0, DRAFT_INSTRUCTIONS_MAX))}
        multiline
        minRows={2}
      />
      <Box>
        <Button
          variant="contained"
          startIcon={<Icons.Sparkles size={iconSize.md} aria-hidden />}
          disabled={draft.isPending}
          onClick={() =>
            draft.mutate({ kind, locale, instructions: instructions.trim() || undefined })
          }
        >
          {draft.isPending ? t('gov.assistant.working') : t('gov.assistant.draft.generate')}
        </Button>
      </Box>
      {draft.isError && (
        <Alert severity="error">
          {apiErrorMessage(draft.error, t('gov.assistant.draft.failed'))}
        </Alert>
      )}
      {draft.data && (
        <Box>
          <AiLabel provenance={draft.data} />
          {draft.data.placeholders.length > 0 && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              {t('gov.assistant.draft.placeholders', {
                placeholders: draft.data.placeholders.join(', '),
              })}
            </Alert>
          )}
          <Stack spacing={2}>
            <TextField
              label={t('gov.assistant.draft.subject')}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <TextField
              label={t('gov.assistant.draft.body')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={10}
            />
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<Icons.Copy size={iconSize.md} aria-hidden />}
                onClick={() => void copy()}
              >
                {t('gov.assistant.draft.copy')}
              </Button>
              <Typography variant="caption" sx={{ color: palette.text.secondary }}>
                {t('gov.assistant.draft.notSent')}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
